import {
  DataFeedRowData,
  DanelConfiguration,
} from '../../../types/generatedTypes';
import { VALID_FEE_TYPES } from './instrumentTypeMapping';

// Process row data with instrument type assignment
export const processRowWithInstrumentType = (
  row: DataFeedRowData,
  index: number,
  config?: DanelConfiguration | null
): DataFeedRowData => {
  let instrumentType = row.instrumentType;

  // Only auto-assign instrumentType if it's completely missing from the database
  if (!instrumentType && config) {
    // Try multi-column matching if configuration is available
    instrumentType = getInstrumentTypeFromMultiColumnMatching(row, config);
  }

  return {
    ...row,
    id: row.id ?? index + 1,
    instrumentType,
  };
};

// Field type checkers
export const isDateField = (key: string): boolean => {
  return (
    key === 'תאריך פעולה' ||
    key === 'תאריך ערך' ||
    key === 'תאריך סליקה כספית' ||
    /^(date|Date)/i.test(key) ||
    key.toLowerCase().includes('date')
  );
};

export const shouldHaveCommas = (key: string): boolean => {
  return (
    key.includes('סכום פעולה') ||
    key.includes('ערך נקוב') ||
    key.includes('שער פעולה') ||
    key.includes('סכום פעולה במטבע התנועה') ||
    key.includes('שווי') ||
    key.includes('סכום') ||
    key.includes('amount') ||
    key.toLowerCase().includes('price') ||
    (key.includes('value') && !key.includes('שער'))
  );
};

// CSS for error highlighting
export const ERROR_HIGHLIGHT_STYLE = `
  .error-highlight {
    animation: errorPulse 2s ease-in-out;
    border: 2px solid #f44336 !important;
    border-radius: 4px;
  }
  
  @keyframes errorPulse {
    0% { box-shadow: 0 0 0 0 rgba(244, 67, 54, 0.7); }
    70% { box-shadow: 0 0 0 10px rgba(244, 67, 54, 0); }
    100% { box-shadow: 0 0 0 0 rgba(244, 67, 54, 0); }
  }
`;

// Check if row matches Deposits and Withdrawals multi-column rules
const checkDepositsWithdrawalsMatch = (
  row: DataFeedRowData,
  config: DanelConfiguration | null
): string | null => {
  const depositsConfig = config?.config_headers?.['Deposits and Withdrawals'];
  const depositsTransformations =
    config?.transformations?.['Deposits and Withdrawals'];

  if (!depositsConfig || !depositsTransformations) {
    return null;
  }

  const depositWithdrawField = depositsConfig['Deposit/Withdraw'];
  const depositWithdrawTransformation =
    depositsTransformations['Deposit/Withdraw'];

  if (
    !depositWithdrawField ||
    !depositWithdrawTransformation?.multiColumnMatching
  ) {
    return null;
  }

  const { column, additionalColumn } = depositWithdrawField;
  const multiColumnRules = depositWithdrawTransformation.multiColumnMatching;

  if (!column || !additionalColumn || !Array.isArray(multiColumnRules)) {
    return null;
  }

  const col1Value = String(row[column] || '').trim();
  const col2Value = String(row[additionalColumn] || '').trim();

  if (!col1Value && !col2Value) {
    return null;
  }

  for (const rule of multiColumnRules) {
    const { standardValue, column1Values = [], column2Values = [] } = rule;

    if (column1Values.length === 0 && column2Values.length === 0) {
      continue;
    }

    const col1Match =
      column1Values.length === 0 ||
      column1Values.some(
        (value: string) =>
          col1Value.toLowerCase() === String(value).toLowerCase().trim()
      );
    const col2Match =
      column2Values.length === 0 ||
      column2Values.some(
        (value: string) =>
          col2Value.toLowerCase() === String(value).toLowerCase().trim()
      );

    if (
      col1Match &&
      col2Match &&
      (standardValue === 'Deposit' || standardValue === 'Withdraw')
    ) {
      return 'Deposits and Withdrawals';
    }
  }

  return null;
};

// Check if row matches Cash Adjustments multi-column rules
const checkCashAdjustmentsMatch = (
  row: DataFeedRowData,
  config: DanelConfiguration | null
): string | null => {
  const cashAdjustmentsConfig = config?.config_headers?.['Cash Adjustments'];
  const cashAdjustmentsTransformations =
    config?.transformations?.['Cash Adjustments'];

  if (!cashAdjustmentsConfig || !cashAdjustmentsTransformations) {
    return null;
  }

  const feeTypeField = cashAdjustmentsConfig['Fee Type'];
  const feeTypeTransformation = cashAdjustmentsTransformations['Fee Type'];

  if (!feeTypeField || !feeTypeTransformation?.multiColumnMatching) {
    return null;
  }

  const { column: feeTypeColumn, additionalColumn: feeTypeAdditionalColumn } =
    feeTypeField;
  const feeTypeMultiColumnRules = feeTypeTransformation.multiColumnMatching;

  if (
    !feeTypeColumn ||
    !feeTypeAdditionalColumn ||
    !Array.isArray(feeTypeMultiColumnRules)
  ) {
    return null;
  }

  const feeTypeCol1Value = String(row[feeTypeColumn] || '').trim();
  const feeTypeCol2Value = String(row[feeTypeAdditionalColumn] || '').trim();

  if (!feeTypeCol1Value && !feeTypeCol2Value) {
    return null;
  }

  for (const rule of feeTypeMultiColumnRules) {
    const { standardValue, column1Values = [], column2Values = [] } = rule;

    if (
      (column1Values.length === 0 && column2Values.length === 0) ||
      !VALID_FEE_TYPES.includes(standardValue)
    ) {
      continue;
    }

    const feeTypeCol1Match =
      column1Values.length === 0 ||
      column1Values.some(
        (value: string) =>
          feeTypeCol1Value.toLowerCase() === String(value).toLowerCase().trim()
      );
    const feeTypeCol2Match =
      column2Values.length === 0 ||
      column2Values.some(
        (value: string) =>
          feeTypeCol2Value.toLowerCase() === String(value).toLowerCase().trim()
      );

    if (feeTypeCol1Match && feeTypeCol2Match) {
      return 'Cash Adjustments';
    }
  }

  return null;
};

// Check if row matches multi-column rules for instrument type detection
export const getInstrumentTypeFromMultiColumnMatching = (
  row: DataFeedRowData,
  config: DanelConfiguration | null
): string | null => {
  try {
    return (
      checkDepositsWithdrawalsMatch(row, config) ||
      checkCashAdjustmentsMatch(row, config)
    );
  } catch (error) {
    // Error in multi-column matching
    return null;
  }
};
